import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  Check,
  Copy,
  Link2,
  Pencil,
  Trash2,
  Unlink,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn, formatNumber, formatPercent } from '../lib/utils';
import {
  DEAL_STAGES,
  dealMatchesDealLibraryActiveQuickFilter,
  dealMatchesDealLibraryUpcomingQuickFilter,
  DEAL_STAGE_VALUES_ORDERED,
  getStageLabel,
  getStageWinProbability,
  STAGE_BADGE_COLORS,
} from '../constants/dealStages';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { buildDealLibraryPatch } from './dealLibraryPatch';
import { formatDateOnly, getDealYear, toDateInput } from './dealLibraryDates';

const CURRENCIES = ['CHF', 'EUR', 'USD'];
const SEGREGATED_STAGES = new Set(['Rejected', 'Archived']);

/** Only non-empty *string* link_group_id counts as linked (library toolbar). */
function dealHasPersistedLinkGroup(deal) {
  const v = deal?.link_group_id;
  if (typeof v !== 'string') return false;
  return v.trim().length > 0;
}

function dealCoreCurrencyKey(deal) {
  return String(deal?.core_currency || 'CHF')
    .trim()
    .toUpperCase();
}

function distinctPersistedLinkGroupIds(deals) {
  return [...new Set(deals.filter(dealHasPersistedLinkGroup).map((d) => String(d.link_group_id).trim()))];
}

// Desktop library: table-fixed + colgroup keeps all columns in view without horizontal scroll.
const DEAL_LIB_TABLE_MIN = 'table-fixed border-separate border-spacing-0';
/** Full-width shell; side padding only (no max-width cap) so laptops use the viewport. */
const DEAL_LIBRARY_PAGE_SHELL =
  'mx-auto flex w-full min-h-0 flex-1 flex-col px-[max(0.75rem,env(safe-area-inset-left))] py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pr-[max(0.75rem,env(safe-area-inset-right))] sm:px-4 sm:py-4 lg:px-5 lg:py-5';
const DEAL_LIBRARY_HEADER_SHELL =
  'mx-auto flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between';
const DEAL_LIBRARY_ACTIONS_COL_PX = 124;
/** Column widths for <colgroup> (must match column count: 14). */
const DEAL_LIB_CUR_COL_PX = 76;
/** Native dd/mm/yyyy date controls need ~140px; labels sit above to keep this width. */
const DEAL_LIB_CLOSE_SR_COL_PX = 148;
const DEAL_LIB_COLGROUP = (
  <colgroup>
    <col style={{ width: 36 }} />
    <col style={{ width: '13%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: 88 }} />
    <col style={{ width: 40 }} />
    <col style={{ width: '7%' }} />
    <col style={{ width: '7%' }} />
    <col style={{ width: 44 }} />
    <col style={{ width: '6%' }} />
    <col style={{ width: 44 }} />
    {/* Cur. must fit the currency select (was 32px → select spilled into Close/SR). */}
    <col style={{ width: DEAL_LIB_CUR_COL_PX }} />
    {/* Close/SR: labels above inputs; width matches a single native date control. */}
    <col style={{ width: DEAL_LIB_CLOSE_SR_COL_PX }} />
    <col style={{ width: 64 }} />
    <col style={{ width: DEAL_LIBRARY_ACTIONS_COL_PX }} />
  </colgroup>
);
// Vertical stick uses the library scroll parent; avoid wrapping <table> in overflow-auto
// (shadcn Table) or sticky will not track the outer scroller.
const STICKY_HEAD_TOP =
  'sticky top-0 z-[21] bg-white shadow-[0_1px_0_0_rgb(229,231,235)]';
const STICKY_CB_TH =
  'sticky left-0 top-0 z-[22] w-10 border-r border-gray-200 bg-white shadow-[0_1px_0_0_rgb(229,231,235)]';
const STICKY_CB_TD =
  'sticky left-0 z-[12] w-10 border-r border-gray-200 bg-white group-hover:bg-slate-50';
const STICKY_CB_SUB = 'sticky left-0 z-[12] w-10 border-r border-indigo-200 bg-indigo-50/90';
const STICKY_ACT_TH =
  'sticky right-0 top-0 z-[22] w-[124px] min-w-[124px] max-w-[124px] border-l border-gray-200 bg-white shadow-[-10px_0_14px_-8px_rgba(15,23,42,0.12),0_1px_0_0_rgb(229,231,235)]';
const STICKY_ACT_TD =
  'sticky right-0 z-[12] w-[124px] min-w-[124px] max-w-[124px] border-l border-gray-200 bg-white shadow-[-10px_0_14px_-8px_rgba(15,23,42,0.1)] group-hover:bg-slate-50';
const STICKY_ACT_SUB =
  'sticky right-0 z-[12] w-[124px] min-w-[124px] max-w-[124px] border-l border-indigo-200 bg-indigo-50/90 shadow-[-10px_0_14px_-8px_rgba(79,70,229,0.12)]';
// Keep in sync with the <TableHead> count below. Used for the segregated-deals
// section banner colSpan so the banner spans the full row without drift on
// column add/remove.
const DEAL_LIBRARY_TABLE_COL_COUNT = 14;

/** @typedef {{ kind: 'deal', deal: object } | { kind: 'subtotal', deals: object[] }} LinkedRow */

/**
 * Group linked deals (same link_group_id) consecutively and append a subtotal row.
 * @param {object[]} deals Pre-sorted slice (active or segregated only).
 * @param {string|null} sortField
 * @param {string} sortDirection
 * @returns {LinkedRow[]}
 */
function buildLinkedTableRows(deals, sortField, sortDirection) {
  if (!deals.length) return [];
  const seen = new Set();
  /** @type {LinkedRow[]} */
  const out = [];
  for (const d of deals) {
    const g = dealHasPersistedLinkGroup(d) ? String(d.link_group_id).trim() : '';
    if (!g) {
      out.push({ kind: 'deal', deal: d });
      continue;
    }
    if (seen.has(g)) continue;
    seen.add(g);
    const members = deals.filter((x) => dealHasPersistedLinkGroup(x) && String(x.link_group_id).trim() === g);
    if (sortField) {
      members.sort((a, b) => compareDeals(a, b, sortField, sortDirection));
    }
    for (const m of members) {
      out.push({ kind: 'deal', deal: m });
    }
    if (members.length > 1) {
      out.push({ kind: 'subtotal', deals: members });
    }
  }
  return out;
}

function computeLinkSubtotals(memberDeals) {
  let rev = 0;
  let cost = 0;
  let invWeighted = 0;
  let probWeighted = 0;
  for (const d of memberDeals) {
    const r = Number(d.total_revenue_chf ?? 0);
    const c = Number(d.total_cost_chf ?? 0);
    rev += r;
    cost += c;
    invWeighted += (Number(d.invoiced_percentage ?? 0) / 100) * r;
    probWeighted += Number(d.pipeline_stage_probability ?? 0) * r;
  }
  const marginPct = rev > 0 ? ((rev - cost) / rev) * 100 : 0;
  const marginAmt = Math.round((rev - cost) * 100) / 100;
  const invPct = rev > 0 ? (invWeighted / rev) * 100 : 0;
  const probDisplay = rev > 0 ? (probWeighted / rev) * 100 : 0;
  const cur = memberDeals[0]?.core_currency || 'CHF';
  return { rev, cost, marginPct, marginAmt, invPct, probDisplay, cur };
}

/**
 * Footer for visible **active** deals only (rejected/archived excluded). Each deal
 * counted once — not linked subtotal rows (those duplicate the same deals).
 * Revenue-weighted invoiced % and stage probability; margin % = (Σrev−Σcost)/Σrev
 * per currency group. Mixed core currencies → one stacked line per currency.
 */
function computeActiveLibraryFooterStats(deals) {
  if (!deals.length) return null;
  const byCur = new Map();
  for (const d of deals) {
    const cur = d.core_currency || 'CHF';
    const r = Number(d.total_revenue_chf ?? 0);
    const c = Number(d.total_cost_chf ?? 0);
    if (!byCur.has(cur)) {
      byCur.set(cur, { rev: 0, cost: 0, invWR: 0, probWR: 0 });
    }
    const o = byCur.get(cur);
    o.rev += r;
    o.cost += c;
    o.invWR += (Number(d.invoiced_percentage ?? 0) / 100) * r;
    o.probWR += Number(d.pipeline_stage_probability ?? 0) * r;
  }
  const currencies = [...byCur.keys()].sort();
  const n = deals.length;
  const lines = currencies.map((cur) => {
    const { rev, cost, invWR, probWR } = byCur.get(cur);
    const marginPct = rev > 0 ? ((rev - cost) / rev) * 100 : 0;
    const marginAmt = Math.round((rev - cost) * 100) / 100;
    const invPct = rev > 0 ? (invWR / rev) * 100 : 0;
    const probDisplay = rev > 0 ? (probWR / rev) * 100 : 0;
    return { cur, rev, cost, marginPct, marginAmt, invPct, probDisplay };
  });
  return { lines, n };
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/** Narrow “Updated” column: two short lines; full stamp in title for hover. */
function UpdatedAtCell({ dateStr, testId }) {
  if (!dateStr) return <span className="text-[10px] text-gray-400">—</span>;
  try {
    const d = new Date(dateStr);
    const day = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return (
      <span
        className="inline-block min-w-[4rem] text-center text-[10px] leading-tight tabular-nums text-gray-400"
        title={formatDate(dateStr)}
        data-testid={testId}
      >
        <span className="block">{day}</span>
        <span className="block text-[9px] text-gray-400/90">{time}</span>
      </span>
    );
  } catch {
    return <span className="text-[10px] text-gray-400">—</span>;
  }
}

/** Compact Close + SR editors: label above each input so the column stays in-bounds. */
function CloseSrDateEditors({ draft, dealId, onChange }) {
  return (
    <div
      className="box-border flex w-full max-w-full min-w-0 flex-col gap-1 overflow-hidden py-0.5"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="presentation"
    >
      <label className="flex min-w-0 max-w-full flex-col gap-0.5">
        <span className="text-[9px] font-semibold uppercase leading-none tracking-wide text-gray-400" title="Deal closure date">
          Close
        </span>
        <Input
          type="date"
          value={draft.deal_closure_date}
          onChange={(e) => onChange('deal_closure_date', e.target.value)}
          className="pl-deal-lib-date-input h-7 w-full min-w-0 max-w-full px-1 text-[10px] leading-none"
          title="Deal closure date (expected close)"
          data-testid={`edit-closure-date-${dealId}`}
        />
      </label>
      <label className="flex min-w-0 max-w-full flex-col gap-0.5">
        <span className="text-[9px] font-semibold uppercase leading-none tracking-wide text-gray-400" title="Service rendered date">
          SR
        </span>
        <Input
          type="date"
          value={draft.service_rendered_date}
          onChange={(e) => onChange('service_rendered_date', e.target.value)}
          className="pl-deal-lib-date-input h-7 w-full min-w-0 max-w-full px-1 text-[10px] leading-none"
          title="Service rendered date"
          data-testid={`edit-service-rendered-date-${dealId}`}
        />
      </label>
    </div>
  );
}

function CloseSrDateReadout({ deal }) {
  return (
    <div className="min-w-0 max-w-full overflow-hidden text-[12px] leading-tight">
      <span className="block truncate" data-testid={`deal-closure-date-${deal.id}`}>
        {formatDateOnly(deal.deal_closure_date)}
      </span>
      <span
        className="block truncate text-[10px] text-gray-400"
        title="Service rendered date"
        data-testid={`deal-service-rendered-date-${deal.id}`}
      >
        SR {formatDateOnly(deal.service_rendered_date)}
      </span>
    </div>
  );
}

function StageBadge({ stage }) {
  const cls = STAGE_BADGE_COLORS[stage] || STAGE_BADGE_COLORS.Demand;
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium leading-none ${cls}`} data-testid="deal-stage-badge">
      {getStageLabel(stage || 'Demand')}
    </span>
  );
}

function MarginCell({ pct, target = 30 }) {
  const color = pct >= target ? 'text-green-600' : pct >= 15 ? 'text-yellow-600' : 'text-red-600';
  return <span className={`font-semibold ${color}`}>{formatPercent(pct)}</span>;
}

function dealMarginAmountChf(deal) {
  const rev = Number(deal?.total_revenue_chf ?? 0);
  const cost = Number(deal?.total_cost_chf ?? 0);
  return Math.round((rev - cost) * 100) / 100;
}

/** Same colour thresholds as ``MarginCell``; amount is deal value − cost (CHF ledger). */
function MarginAmtCell({ amountChf, marginPct, target = 30 }) {
  const color = marginPct >= target ? 'text-green-600' : marginPct >= 15 ? 'text-yellow-600' : 'text-red-600';
  return (
    <span className={`whitespace-nowrap font-mono text-xs font-semibold tabular-nums ${color}`} title="Margin amount (deal value − cost)">
      {formatNumber(amountChf)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Column sorting
// ---------------------------------------------------------------------------
// Sort keys map to an accessor that returns a primitive value for comparison.
// Numeric accessors return finite numbers (so NaN doesn't poison the compare);
// string accessors return a lowercased string; date accessors return a
// timestamp (NaN-safe via `Number.NEGATIVE_INFINITY` fallback).
//
// When sortConfig.field is null the table falls back to its natural server
// order (updated_at desc). Sorting is applied INDEPENDENTLY within the active
// and segregated sections so the two-section layout is preserved.
const DEAL_SORT_ACCESSORS = {
  project: (d) => (d.project_name || '').toLowerCase(),
  client: (d) => (d.client_name || '').toLowerCase(),
  // Sort stages by pipeline position, not alphabetically:
  // Demand → Quoted → Approved → Partial → Invoiced → Paid → Rejected → Archived.
  // Unknown stages land after all known ones so they don't silently intermix.
  stage: (d) => {
    const idx = DEAL_STAGE_VALUES_ORDERED.indexOf(d.stage || 'Demand');
    return idx === -1 ? DEAL_STAGE_VALUES_ORDERED.length : idx;
  },
  probability: (d) => Number(d.pipeline_stage_probability ?? 0),
  dealValue: (d) => Number(d.total_revenue_chf ?? 0),
  cost: (d) => Number(d.total_cost_chf ?? 0),
  margin: (d) => {
    const rev = Number(d.total_revenue_chf ?? 0);
    const cost = Number(d.total_cost_chf ?? 0);
    return rev > 0 ? ((rev - cost) / rev) * 100 : 0;
  },
  marginAmt: (d) => {
    const rev = Number(d.total_revenue_chf ?? 0);
    const cost = Number(d.total_cost_chf ?? 0);
    return rev - cost;
  },
  invoiced: (d) => Number(d.invoiced_percentage ?? 0),
  currency: (d) => (d.core_currency || 'CHF').toLowerCase(),
  expectedClose: (d) => {
    const t = Date.parse(d.deal_closure_date || '');
    return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
  },
  updated: (d) => {
    const t = Date.parse(d.updated_at || '');
    return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
  },
};

function compareDeals(a, b, field, direction) {
  const accessor = DEAL_SORT_ACCESSORS[field];
  if (!accessor) return 0;
  const va = accessor(a);
  const vb = accessor(b);
  let cmp;
  if (typeof va === 'number' && typeof vb === 'number') {
    cmp = va - vb;
  } else {
    cmp = String(va).localeCompare(String(vb));
  }
  return direction === 'desc' ? -cmp : cmp;
}

function SortableHeader({ field, sortConfig, onSort, align = 'left', className = '', title, children }) {
  const active = sortConfig.field === field;
  const dir = active ? sortConfig.direction : null;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  const justify = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  return (
    <TableHead className={`${STICKY_HEAD_TOP} ${className}`} title={title}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`group inline-flex items-center gap-1 ${justify} w-full cursor-pointer select-none bg-transparent p-0 text-inherit hover:text-gray-900`}
        data-testid={`deal-sort-${field}`}
        data-sort-active={active ? dir : 'none'}
      >
        <span>{children}</span>
        <Icon
          className={`h-3 w-3 transition-opacity ${active ? 'opacity-100 text-gray-700' : 'opacity-40 group-hover:opacity-80'}`}
          aria-hidden="true"
        />
      </button>
    </TableHead>
  );
}

const QUICK_DEFAULT = {
  project_name: '',
  client_name: '',
  stage: 'Demand',
  deal_value: '',
  deal_cost: '',
  currency: 'CHF',
  deal_closure_date: '',
  service_rendered_date: '',
  probability_override: '',
  notes: '',
};

export function DealLibraryPage({
  deals,
  contextBanner = null,
  onSelect,
  onDelete,
  onDuplicate,
  onPatchDeal,
  onBulkLinkDeals,
  onCreateQuickDeal,
  loading,
  onClose,
}) {
  const [search, setSearch] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  /** Quick filter: Approved / Partial / Invoiced with invoiced % under 100 (see deal_stages.deal_matches_deal_library_active_quick_filter). */
  const [activeQuickFilterOn, setActiveQuickFilterOn] = useState(false);
  /** Quick filter: Demand / Quote Issued — sales pipeline (see deal_stages.deal_matches_deal_library_upcoming_quick_filter). */
  const [upcomingQuickFilterOn, setUpcomingQuickFilterOn] = useState(false);
  // Tri-state sort: { field: null } → default (server order, updated_at desc);
  // clicking a column cycles field=X asc → desc → cleared.
  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });
  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.field !== field) return { field, direction: 'asc' };
      if (prev.direction === 'asc') return { field, direction: 'desc' };
      return { field: null, direction: 'asc' };
    });
  };
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickDraft, setQuickDraft] = useState(QUICK_DEFAULT);
  const [creatingQuick, setCreatingQuick] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const projectOptions = useMemo(() => {
    return Array.from(
      new Set(
        deals
          .map((deal) => (deal.project_name || '').trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [deals]);

  const clientOptions = useMemo(() => {
    return Array.from(
      new Set(
        deals
          .map((deal) => (deal.client_name || '').trim())
          .filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [deals]);

  const yearOptions = useMemo(() => {
    return Array.from(
      new Set(
        deals
          .map((deal) => getDealYear(deal.deal_closure_date))
          .filter(Boolean),
      ),
    ).sort((a, b) => Number(b) - Number(a));
  }, [deals]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = deals.filter((deal) => {
      const projectName = (deal.project_name || '').trim();
      const clientName = (deal.client_name || '').trim();
      const stageValue = deal.stage || 'Demand';
      const yearValue = getDealYear(deal.deal_closure_date);

      if (projectFilter !== 'all' && projectName !== projectFilter) return false;
      if (clientFilter !== 'all' && clientName !== clientFilter) return false;
      if (stageFilter !== 'all' && stageValue !== stageFilter) return false;
      if (yearFilter !== 'all' && yearValue !== yearFilter) return false;
      if (activeQuickFilterOn && !dealMatchesDealLibraryActiveQuickFilter(deal.stage, deal.invoiced_percentage)) return false;
      if (upcomingQuickFilterOn && !dealMatchesDealLibraryUpcomingQuickFilter(deal.stage)) return false;
      if (!q) return true;

      const searchFields = [
        deal.id,
        projectName,
        clientName,
        stageValue,
        getStageLabel(stageValue),
        deal.core_currency || 'CHF',
        deal.deal_closure_date || '',
        deal.updated_at || '',
        deal.notes || '',
        deal.total_revenue_chf,
        deal.total_cost_chf,
        deal.invoiced_percentage,
        deal.pipeline_stage_probability,
      ];

      return searchFields.some((fieldValue) =>
        String(fieldValue ?? '')
          .toLowerCase()
          .includes(q),
      );
    });
    const activeDeals = [];
    const segregatedDeals = [];
    for (const deal of matched) {
      if (SEGREGATED_STAGES.has(deal.stage || 'Demand')) segregatedDeals.push(deal);
      else activeDeals.push(deal);
    }
    // Apply sort per-section so the segregated banner stays between groups.
    if (sortConfig.field) {
      const cmp = (a, b) => compareDeals(a, b, sortConfig.field, sortConfig.direction);
      activeDeals.sort(cmp);
      segregatedDeals.sort(cmp);
    }
    return [...activeDeals, ...segregatedDeals];
  }, [deals, search, projectFilter, clientFilter, stageFilter, yearFilter, activeQuickFilterOn, upcomingQuickFilterOn, sortConfig]);

  // Drop checkbox selections for rows that are no longer in the filtered list (e.g. after
  // changing search or filters). Otherwise selectedIds can stay larger than selectedInView
  // and canLink's equality check keeps "Link selected" disabled even for valid visible picks.
  useEffect(() => {
    const allowed = new Set(filtered.map((d) => d.id));
    setSelectedIds((prev) => {
      let removed = false;
      const next = new Set();
      for (const id of prev) {
        if (allowed.has(id)) next.add(id);
        else removed = true;
      }
      return removed ? next : prev;
    });
  }, [filtered]);

  const segregatedCount = useMemo(
    () => filtered.reduce((count, deal) => (SEGREGATED_STAGES.has(deal.stage || 'Demand') ? count + 1 : count), 0),
    [filtered],
  );
  const activeCount = filtered.length - segregatedCount;

  const activeDealSlice = useMemo(() => filtered.slice(0, activeCount), [filtered, activeCount]);
  const segregatedDealSlice = useMemo(() => filtered.slice(activeCount), [filtered, activeCount]);

  const activeFooterStats = useMemo(
    () => computeActiveLibraryFooterStats(activeDealSlice),
    [activeDealSlice],
  );

  const activeTableRows = useMemo(
    () => buildLinkedTableRows(activeDealSlice, sortConfig.field, sortConfig.direction),
    [activeDealSlice, sortConfig.field, sortConfig.direction],
  );
  const segregatedTableRows = useMemo(
    () => buildLinkedTableRows(segregatedDealSlice, sortConfig.field, sortConfig.direction),
    [segregatedDealSlice, sortConfig.field, sortConfig.direction],
  );

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [linkBusy, setLinkBusy] = useState(false);

  const confirmDeleteDeal = useCallback((dealId) => {
    if (!dealId) return;
    const ok = window.confirm('Are you sure you want to delete this deal?');
    if (!ok) return;
    onDelete(dealId);
  }, [onDelete]);

  const selectedInView = useMemo(
    () => filtered.filter((d) => selectedIds.has(d.id)),
    [filtered, selectedIds],
  );

  const canLink = useMemo(() => {
    const sel = selectedInView;
    if (sel.length < 2) return false;
    if (new Set(sel.map((d) => dealCoreCurrencyKey(d))).size !== 1) return false;
    const gids = distinctPersistedLinkGroupIds(sel);
    if (gids.length > 1) return false;
    if (sel.every((d) => dealHasPersistedLinkGroup(d))) return false;
    const allSeg = sel.every((d) => SEGREGATED_STAGES.has(d.stage || 'Demand'));
    const allAct = sel.every((d) => !SEGREGATED_STAGES.has(d.stage || 'Demand'));
    return allSeg || allAct;
  }, [selectedInView]);

  const canUnlink = useMemo(
    () => selectedInView.length > 0 && selectedInView.every((d) => dealHasPersistedLinkGroup(d)),
    [selectedInView],
  );

  const toggleSelect = (dealId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(dealId);
      else next.delete(dealId);
      return next;
    });
  };

  const allSelectableIds = useMemo(() => {
    const ids = [];
    for (const row of activeTableRows) {
      if (row.kind === 'deal') ids.push(row.deal.id);
    }
    for (const row of segregatedTableRows) {
      if (row.kind === 'deal') ids.push(row.deal.id);
    }
    return ids;
  }, [activeTableRows, segregatedTableRows]);

  const allSelected = useMemo(
    () => allSelectableIds.length > 0 && allSelectableIds.every((id) => selectedIds.has(id)),
    [allSelectableIds, selectedIds],
  );

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(allSelectableIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const linkSelected = async () => {
    if (selectedInView.length < 2) return;
    if (!onBulkLinkDeals && !onPatchDeal) return;
    const currencies = new Set(selectedInView.map((d) => dealCoreCurrencyKey(d)));
    if (currencies.size !== 1) {
      toast.error('Linked deals must use the same currency.');
      return;
    }
    const gids = distinctPersistedLinkGroupIds(selectedInView);
    if (gids.length > 1) {
      toast.error('Select deals from at most one existing link group.');
      return;
    }
    const adoptLinkGroupId = gids.length === 1 ? gids[0] : undefined;
    const ids = selectedInView.map((d) => d.id);
    setLinkBusy(true);
    try {
      if (onBulkLinkDeals) {
        await onBulkLinkDeals(ids, { adoptLinkGroupId });
      } else {
        const gid =
          adoptLinkGroupId
            || (typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : `lg-${Date.now()}-${Math.random().toString(16).slice(2)}`);
        for (const id of ids) {
          await onPatchDeal(id, { link_group_id: gid });
        }
      }
      setSelectedIds(new Set());
      toast.success(`Linked ${ids.length} projects`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg =
        typeof d === 'string'
          ? d
          : Array.isArray(d)
            ? d.map((x) => x.msg || x.detail || JSON.stringify(x)).join('; ')
            : e?.message || 'Could not link deals';
      toast.error(msg);
    } finally {
      setLinkBusy(false);
    }
  };

  const unlinkSelected = async () => {
    if (!onPatchDeal || !selectedInView.length) return;
    setLinkBusy(true);
    try {
      for (const d of selectedInView) {
        await onPatchDeal(d.id, { link_group_id: null });
      }
      setSelectedIds(new Set());
      toast.success('Unlinked selected project(s)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not unlink deals');
    } finally {
      setLinkBusy(false);
    }
  };

  const mobileRowsFlat = useMemo(() => {
    const out = [];
    for (const r of activeTableRows) {
      if (r.kind === 'deal') out.push({ kind: 'deal', deal: r.deal });
      else out.push({ kind: 'subtotal', deals: r.deals });
    }
    if (activeDealSlice.length > 0) {
      out.push({ kind: 'active_footer' });
    }
    if (segregatedCount > 0) {
      out.push({ kind: 'banner' });
    }
    for (const r of segregatedTableRows) {
      if (r.kind === 'deal') out.push({ kind: 'deal', deal: r.deal });
      else out.push({ kind: 'subtotal', deals: r.deals });
    }
    return out;
  }, [activeTableRows, segregatedTableRows, segregatedCount, activeDealSlice.length]);

  const beginEdit = (deal) => {
    setEditingId(deal.id);
    setEditDraft({
      project_name: deal.project_name || '',
      client_name: deal.client_name || '',
      stage: deal.stage || 'Demand',
      deal_value_chf: String(deal.total_revenue_chf ?? 0),
      deal_cost_chf: String(deal.total_cost_chf ?? 0),
      deal_closure_date: toDateInput(deal.deal_closure_date),
      service_rendered_date: toDateInput(deal.service_rendered_date),
      core_currency: deal.core_currency || 'CHF',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const saveEdit = async (dealId) => {
    if (!editDraft || !onPatchDeal) return;
    const originalDeal = deals.find((d) => d.id === dealId);
    const valueNum = Number(editDraft.deal_value_chf);
    const costNum = Number(editDraft.deal_cost_chf);
    if (!Number.isFinite(valueNum) || valueNum < 0 || !Number.isFinite(costNum) || costNum < 0) {
      return;
    }
    let rejectionComment = null;
    if (editDraft.stage === 'Rejected' && originalDeal?.stage !== 'Rejected') {
      const input = window.prompt('Add rejection reason (required):');
      if (input == null) return;
      const trimmed = input.trim();
      if (!trimmed) return;
      rejectionComment = trimmed;
    }
    setSavingEdit(true);
    try {
      // Totals are included only when those inputs differ from the loaded deal.
      // Quick-adjust math stays on the server; an unchanged total must not be sent.
      await onPatchDeal(dealId, buildDealLibraryPatch(originalDeal, editDraft, rejectionComment));
      cancelEdit();
      toast.success('Deal updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const createQuick = async () => {
    if (!onCreateQuickDeal) return;
    const dealValue = Number(quickDraft.deal_value);
    const dealCost = quickDraft.deal_cost === '' ? 0 : Number(quickDraft.deal_cost);
    if (!quickDraft.project_name.trim() || !Number.isFinite(dealValue) || dealValue < 0 || !Number.isFinite(dealCost) || dealCost < 0) {
      return;
    }
    setCreatingQuick(true);
    try {
      await onCreateQuickDeal({
        project_name: quickDraft.project_name.trim(),
        client_name: quickDraft.client_name.trim(),
        stage: quickDraft.stage,
        deal_value: dealValue,
        deal_cost: dealCost,
        currency: quickDraft.currency,
        deal_closure_date: quickDraft.deal_closure_date || null,
        service_rendered_date: quickDraft.service_rendered_date || null,
        probability_override: quickDraft.probability_override === '' ? null : Number(quickDraft.probability_override),
        notes: quickDraft.notes || null,
      });
      setQuickDraft(QUICK_DEFAULT);
      setQuickOpen(false);
    } finally {
      setCreatingQuick(false);
    }
  };

  return (
    <div
      className="flex min-h-0 flex-1 flex-col bg-slate-50 touch-manipulation"
      data-testid="load-deal-dialog"
    >
      <header className="shrink-0 border-b border-gray-200 bg-white px-[max(0.75rem,env(safe-area-inset-left))] py-3 pr-[max(0.75rem,env(safe-area-inset-right))] pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-4 sm:py-4 lg:px-5">
        <div className={DEAL_LIBRARY_HEADER_SHELL}>
          <div className="flex min-w-0 items-start gap-2 sm:gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 min-w-11 shrink-0 gap-1.5 px-3 sm:h-9 sm:min-w-0"
              onClick={onClose}
              data-testid="deal-library-back"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0">
              <h1 className="pl-page-title flex items-center gap-2">
                Deal library
              </h1>
              <p className="mt-0.5 hidden text-xs text-gray-500 sm:block">
                Operational workspace: quick-create, inline edit, duplicate/delete, then click any row to open the full deal editor.
              </p>
              <button
                type="button"
                onClick={() => setQuickOpen((v) => !v)}
                className="mt-1 text-[11px] font-medium text-slate-600 underline decoration-slate-300 underline-offset-2 hover:text-slate-900 sm:mt-1"
                data-testid="deal-library-quick-toggle"
              >
                {quickOpen ? 'Close quick create' : 'Quick create'}
              </button>
              {contextBanner ? (
                <p
                  className="mt-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs leading-relaxed text-sky-950 sm:mt-3 sm:text-sm"
                  data-testid="deal-library-context-banner"
                >
                  {contextBanner}
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {deals.length > 0 && (
              <div className="text-sm tabular-nums text-gray-500 sm:text-right" data-testid="load-deal-count">
                <span className="font-medium text-gray-900">{activeCount}</span>
                {' active / '}
                <span className="font-medium text-gray-900">{segregatedCount}</span>
                {' segregated (of '}
                <span className="font-medium text-gray-900">{filtered.length}</span>
                {') '}
                {' deal'}
                {filtered.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className={DEAL_LIBRARY_PAGE_SHELL}>
        {quickOpen && (
          <div className="mb-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm sm:mb-4 sm:p-4" data-testid="deal-library-quick-entry">
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <Input
                placeholder="Project *"
                value={quickDraft.project_name}
                onChange={(e) => setQuickDraft((d) => ({ ...d, project_name: e.target.value }))}
              />
              <Input
                placeholder="Client"
                value={quickDraft.client_name}
                onChange={(e) => setQuickDraft((d) => ({ ...d, client_name: e.target.value }))}
              />
              <Select value={quickDraft.stage} onValueChange={(v) => setQuickDraft((d) => ({ ...d, stage: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEAL_STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={quickDraft.currency} onValueChange={(v) => setQuickDraft((d) => ({ ...d, currency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Deal value *"
                value={quickDraft.deal_value}
                onChange={(e) => setQuickDraft((d) => ({ ...d, deal_value: e.target.value }))}
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Cost"
                value={quickDraft.deal_cost}
                onChange={(e) => setQuickDraft((d) => ({ ...d, deal_cost: e.target.value }))}
              />
              <Input
                type="date"
                value={quickDraft.deal_closure_date}
                onChange={(e) => setQuickDraft((d) => ({ ...d, deal_closure_date: e.target.value }))}
                title="Expected close (deal closure date)"
              />
              <Input
                type="date"
                value={quickDraft.service_rendered_date}
                onChange={(e) => setQuickDraft((d) => ({ ...d, service_rendered_date: e.target.value }))}
                title="Service rendered (optional — uninvoiced AR timetable)"
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                max="1"
                placeholder="Probability override (opt.)"
                value={quickDraft.probability_override}
                onChange={(e) => setQuickDraft((d) => ({ ...d, probability_override: e.target.value }))}
              />
            </div>
            <Input
              className="mt-2"
              placeholder="Notes (optional)"
              value={quickDraft.notes}
              onChange={(e) => setQuickDraft((d) => ({ ...d, notes: e.target.value }))}
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { setQuickOpen(false); setQuickDraft(QUICK_DEFAULT); }}>
                Cancel
              </Button>
              <Button type="button" onClick={createQuick} disabled={creatingQuick || loading}>
                {creatingQuick ? 'Creating…' : 'Create quick deal'}
              </Button>
            </div>
          </div>
        )}

        <div className="mb-3 shrink-0 sm:mb-4">
          <div className="flex flex-col gap-2 lg:flex-row lg:flex-nowrap lg:items-center">
            <div className="relative lg:w-[340px] lg:min-w-[300px]">
              <Input
                placeholder="Search across project, client, stage, currency, values, dates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 bg-white pl-3 text-sm shadow-sm"
                data-testid="load-deal-search"
                autoFocus
              />
            </div>
            <div className="flex items-center justify-end lg:hidden">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 px-2.5 text-xs"
                onClick={() => setMobileFiltersOpen((v) => !v)}
                data-testid="deal-library-mobile-filters-toggle"
              >
                {mobileFiltersOpen ? 'Hide filters' : 'Filters'}
              </Button>
            </div>
            <div className={`${mobileFiltersOpen ? 'grid' : 'hidden'} grid-cols-1 gap-2 lg:grid lg:grid-cols-none lg:gap-0 lg:contents`}>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="h-9 bg-white lg:w-[180px] lg:min-w-[160px]" data-testid="deal-filter-project">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projectOptions.map((project) => (
                  <SelectItem key={project} value={project}>
                    {project}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="h-9 bg-white lg:w-[180px] lg:min-w-[160px]" data-testid="deal-filter-client">
                <SelectValue placeholder="All clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All clients</SelectItem>
                {clientOptions.map((client) => (
                  <SelectItem key={client} value={client}>
                    {client}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="h-9 bg-white lg:w-[180px] lg:min-w-[160px]" data-testid="deal-filter-stage">
                <SelectValue placeholder="All stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stages</SelectItem>
                {DEAL_STAGES.map((stage) => (
                  <SelectItem key={stage.value} value={stage.value}>
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="h-9 bg-white lg:w-[160px] lg:min-w-[140px]" data-testid="deal-filter-year">
                <SelectValue placeholder="All years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={year}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => {
                setActiveQuickFilterOn((v) => {
                  const next = !v;
                  if (next) setUpcomingQuickFilterOn(false);
                  return next;
                });
              }}
              aria-pressed={activeQuickFilterOn}
              title="Approved, Partial, or Invoiced with invoiced % below 100%"
              className={cn(
                'inline-flex h-9 shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-input bg-white px-3 text-sm shadow-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
                activeQuickFilterOn
                  ? 'border-slate-800 bg-slate-100 font-medium text-slate-900'
                  : 'font-normal text-foreground hover:bg-slate-50',
              )}
              data-testid="deal-filter-active-quick"
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => {
                setUpcomingQuickFilterOn((v) => {
                  const next = !v;
                  if (next) setActiveQuickFilterOn(false);
                  return next;
                });
              }}
              aria-pressed={upcomingQuickFilterOn}
              title="Demand and Quote Issued — deals that still need sales work"
              className={cn(
                'inline-flex h-9 shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-input bg-white px-3 text-sm shadow-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
                upcomingQuickFilterOn
                  ? 'border-slate-800 bg-slate-100 font-medium text-slate-900'
                  : 'font-normal text-foreground hover:bg-slate-50',
              )}
              data-testid="deal-filter-upcoming-quick"
            >
              Upcoming
            </button>
            </div>
          </div>
        </div>

        {filtered.length > 0 ? (
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center" data-testid="deal-library-link-toolbar">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1 text-xs"
                disabled={!canLink || linkBusy || loading}
                onClick={linkSelected}
                data-testid="deal-library-link-selected"
              >
                <Link2 className="h-3.5 w-3.5" aria-hidden />
                Link selected
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1 text-xs"
                disabled={!canUnlink || linkBusy || loading}
                onClick={unlinkSelected}
                data-testid="deal-library-unlink-selected"
              >
                <Unlink className="h-3.5 w-3.5" aria-hidden />
                Unlink
              </Button>
            </div>
            <p className="text-[11px] leading-snug text-gray-500">
              Select two or more visible deals with the same currency (all active or all segregated), then link. Unlinked only, or one existing link group plus extra unlinked rows (adds them to that group). Not two different groups. A subtotal row sums deal value, cost, margin %, and revenue-weighted invoiced % and probability.
            </p>
          </div>
        ) : null}

        <div className="pl-table-shell flex min-h-0 flex-1 flex-col overflow-hidden shadow-sm [-webkit-overflow-scrolling:touch]">
          {filtered.length === 0 ? (
            <p className="py-16 text-center text-sm text-gray-500" data-testid="load-deal-empty">
              {deals.length === 0 ? 'No saved deals found.' : 'No deals match your search or filters.'}
            </p>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto overscroll-contain" data-testid="load-deal-scroll">
              <div className="space-y-3 p-3 lg:hidden">
                {mobileRowsFlat.map((entry, idx) => {
                  if (entry.kind === 'active_footer') {
                    if (!activeFooterStats) return null;
                    return (
                      <div
                        key={`mobile-footer-${idx}`}
                        className="rounded-lg border-2 border-slate-300 bg-slate-100/95 p-3 shadow-sm"
                        data-testid="deal-library-active-total-mobile"
                      >
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-800">Total · visible active</p>
                        <p className="mt-0.5 text-[10px] leading-snug text-slate-600">
                          {activeFooterStats.n} deal{activeFooterStats.n === 1 ? '' : 's'} · excludes Rejected/Archived; each deal once (not linked subtotal rows). Invoiced % and
                          probability are revenue-weighted per currency.
                        </p>
                        <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          {activeFooterStats.lines.map((ln, li) => (
                            <Fragment key={ln.cur}>
                              <div className={`col-span-2 ${li > 0 ? 'mt-2 border-t border-slate-200/80 pt-2' : ''}`}>
                                <span className="font-semibold text-slate-800">{ln.cur}</span>
                              </div>
                              <div>
                                <dt className="text-slate-600">Deal value</dt>
                                <dd className="font-mono font-semibold text-slate-900">{formatNumber(ln.rev)} {ln.cur}</dd>
                              </div>
                              <div>
                                <dt className="text-slate-600">Cost</dt>
                                <dd className="font-mono font-semibold text-slate-900">{formatNumber(ln.cost)} {ln.cur}</dd>
                              </div>
                              <div>
                                <dt className="text-slate-600">Margin %</dt>
                                <dd>
                                  <MarginCell pct={ln.marginPct} target={30} />
                                </dd>
                              </div>
                              <div>
                                <dt className="text-slate-600">Margin amt</dt>
                                <dd className="font-mono">
                                  <MarginAmtCell amountChf={ln.marginAmt} marginPct={ln.marginPct} target={30} /> {ln.cur}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-slate-600">Invoiced % (wtd.)</dt>
                                <dd className="font-mono font-semibold">{formatPercent(ln.invPct)}</dd>
                              </div>
                              <div>
                                <dt className="text-slate-600">Stage prob. (wtd.)</dt>
                                <dd className="font-mono font-semibold">{formatPercent(ln.probDisplay)}</dd>
                              </div>
                            </Fragment>
                          ))}
                        </dl>
                      </div>
                    );
                  }
                  if (entry.kind === 'banner') {
                    return (
                      <div
                        key={`mobile-banner-${idx}`}
                        className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-600"
                      >
                        Segregated deals (Rejected / Archived)
                      </div>
                    );
                  }
                  if (entry.kind === 'subtotal') {
                    const st = computeLinkSubtotals(entry.deals);
                    const gid = String(entry.deals[0]?.link_group_id || '').slice(0, 8);
                    return (
                      <div
                        key={`mobile-sub-${idx}-${gid}`}
                        className="rounded-lg border border-indigo-200 bg-indigo-50/90 p-3 text-xs shadow-sm"
                        data-testid="deal-link-subtotal-mobile"
                      >
                        <p className="font-semibold text-indigo-950">Linked subtotal ({entry.deals.length} projects)</p>
                        <dl className="mt-2 grid grid-cols-2 gap-2 font-mono text-indigo-950">
                          <div>
                            <dt className="text-indigo-700/90">Deal value</dt>
                            <dd>{formatNumber(st.rev)} {st.cur}</dd>
                          </div>
                          <div>
                            <dt className="text-indigo-700/90">Cost</dt>
                            <dd>{formatNumber(st.cost)} {st.cur}</dd>
                          </div>
                          <div>
                            <dt className="text-indigo-700/90">Margin %</dt>
                            <dd><MarginCell pct={st.marginPct} target={30} /></dd>
                          </div>
                          <div>
                            <dt className="text-indigo-700/90">Margin amt</dt>
                            <dd>
                              <MarginAmtCell amountChf={st.marginAmt} marginPct={st.marginPct} target={30} />{' '}
                              <span className="text-indigo-700/80">{st.cur}</span>
                            </dd>
                          </div>
                          <div>
                            <dt className="text-indigo-700/90">Invoiced %</dt>
                            <dd>{formatPercent(st.invPct)}</dd>
                          </div>
                          <div className="col-span-2">
                            <dt className="text-indigo-700/90">Weighted probability</dt>
                            <dd>{formatPercent(st.probDisplay)}</dd>
                          </div>
                        </dl>
                      </div>
                    );
                  }
                  const deal = entry.deal;
                  const isEditing = editingId === deal.id;
                  const draft = isEditing ? editDraft : null;
                  const draftRevenue = Number(draft?.deal_value_chf ?? deal.total_revenue_chf ?? 0);
                  const draftCost = Number(draft?.deal_cost_chf ?? deal.total_cost_chf ?? 0);
                  const marginPct = draftRevenue > 0 ? ((draftRevenue - draftCost) / draftRevenue) * 100 : 0;
                  const stageValue = draft?.stage || deal.stage || 'Demand';
                  const stageP = isEditing ? getStageWinProbability(stageValue) : (deal.pipeline_stage_probability ?? 0);
                  const rowCurrency = (isEditing ? draft?.core_currency : deal.core_currency) || 'CHF';

                  return (
                    <Fragment key={`mobile-${deal.id}`}>
                      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm" data-testid={`deal-row-mobile-${deal.id}`}>
                        <div
                          className="mb-2 flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          role="presentation"
                        >
                          <Checkbox
                            checked={selectedIds.has(deal.id)}
                            onCheckedChange={(v) => toggleSelect(deal.id, v === true)}
                            disabled={loading || linkBusy}
                            aria-label={`Select ${deal.project_name || 'deal'}`}
                          />
                          <span className="text-[10px] uppercase tracking-wide text-gray-400">Select to link</span>
                        </div>
                        {isEditing ? (
                          <div className="space-y-2">
                            <Input
                              value={draft.project_name}
                              onChange={(e) => setEditDraft((d) => ({ ...d, project_name: e.target.value }))}
                              className="h-9"
                            />
                            <Input
                              value={draft.client_name}
                              onChange={(e) => setEditDraft((d) => ({ ...d, client_name: e.target.value }))}
                              className="h-9"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <Select value={draft.stage} onValueChange={(v) => setEditDraft((d) => ({ ...d, stage: v }))}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {DEAL_STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Select value={draft.core_currency} onValueChange={(v) => setEditDraft((d) => ({ ...d, core_currency: v }))}>
                                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={draft.deal_value_chf}
                                onChange={(e) => setEditDraft((d) => ({ ...d, deal_value_chf: e.target.value }))}
                                className="h-9"
                              />
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={draft.deal_cost_chf}
                                onChange={(e) => setEditDraft((d) => ({ ...d, deal_cost_chf: e.target.value }))}
                                className="h-9"
                              />
                            </div>
                            <CloseSrDateEditors
                              draft={draft}
                              dealId={deal.id}
                              onChange={(field, value) => setEditDraft((d) => ({ ...d, [field]: value }))}
                            />
                            <div className="flex items-center justify-end gap-2">
                              <Button type="button" variant="outline" onClick={cancelEdit} disabled={savingEdit || loading}>
                                Cancel
                              </Button>
                              <Button type="button" onClick={() => saveEdit(deal.id)} disabled={savingEdit || loading}>
                                Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-gray-900">{deal.project_name || 'Untitled'}</p>
                                <p className="truncate text-xs text-gray-500">{deal.client_name || '\u2014'}</p>
                              </div>
                              <StageBadge stage={deal.stage} />
                            </div>
                            <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <dt className="text-gray-400">Stage p</dt>
                                <dd className="font-mono text-gray-800">{formatPercent(stageP * 100)}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400">Margin</dt>
                                <dd><MarginCell pct={marginPct} target={deal.margin_target_pct || 30} /></dd>
                              </div>
                              <div>
                                <dt className="text-gray-400">Margin amt</dt>
                                <dd className="font-mono text-gray-800">
                                  <MarginAmtCell
                                    amountChf={dealMarginAmountChf(deal)}
                                    marginPct={marginPct}
                                    target={deal.margin_target_pct || 30}
                                  />{' '}
                                  {rowCurrency}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-gray-400">Deal value</dt>
                                <dd className="font-mono text-gray-800">{formatNumber(deal.total_revenue_chf)} {rowCurrency}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400">Cost</dt>
                                <dd className="font-mono text-gray-800">{formatNumber(deal.total_cost_chf)} {rowCurrency}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400">Invoiced</dt>
                                <dd className="font-mono text-gray-800">{formatPercent(deal.invoiced_percentage ?? 0)}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400">Deal closure</dt>
                                <dd className="text-gray-700">{formatDateOnly(deal.deal_closure_date)}</dd>
                              </div>
                              <div>
                                <dt className="text-gray-400">Service rendered</dt>
                                <dd className="text-gray-700">{formatDateOnly(deal.service_rendered_date)}</dd>
                              </div>
                            </dl>
                            <div className="mt-3 grid grid-cols-4 gap-1.5">
                              <Button type="button" variant="ghost" size="icon" onClick={() => beginEdit(deal)} disabled={loading} title="Inline edit">
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button type="button" variant="ghost" size="icon" onClick={() => onSelect(deal.id)} disabled={loading} title="Open full editor">
                                <ArrowLeft className="h-3.5 w-3.5 rotate-180" />
                              </Button>
                              <Button type="button" variant="ghost" size="icon" onClick={() => onDuplicate(deal.id)} disabled={loading} title="Duplicate deal">
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                              <Button type="button" variant="ghost" size="icon" onClick={() => confirmDeleteDeal(deal.id)} disabled={loading} title="Delete deal">
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            </div>
                            <p className="mt-2 text-[11px] text-gray-400">{formatDate(deal.updated_at)}</p>
                          </>
                        )}
                      </div>
                    </Fragment>
                  );
                })}
              </div>

              <div className="hidden lg:block">
                <div className="relative w-full min-w-0">
                  <table data-testid="load-deal-table" className={`w-full caption-bottom text-sm ${DEAL_LIB_TABLE_MIN}`}>
                  {DEAL_LIB_COLGROUP}
                  <TableHeader className="border-b-0 bg-transparent [&_tr]:border-b-0">
                    <TableRow className="bg-white hover:bg-white">
                      <TableHead className={`pl-table-head-cell text-center ${STICKY_CB_TH}`}>
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={(v) => toggleSelectAll(v === true)}
                          disabled={loading || linkBusy || allSelectableIds.length === 0}
                          aria-label="Select all visible deals"
                        />
                      </TableHead>
                      <SortableHeader field="project" sortConfig={sortConfig} onSort={handleSort} align="left" className="pl-table-head-cell min-w-0 pl-wrap px-1 text-left">Project</SortableHeader>
                      <SortableHeader field="client" sortConfig={sortConfig} onSort={handleSort} align="left" className="pl-table-head-cell min-w-0 pl-wrap px-1 text-left">Client</SortableHeader>
                      <SortableHeader field="stage" sortConfig={sortConfig} onSort={handleSort} align="center" className="pl-table-head-cell px-0.5 pl-no-wrap text-center">Stage</SortableHeader>
                      <SortableHeader field="probability" sortConfig={sortConfig} onSort={handleSort} align="center" className="pl-table-head-cell px-0.5 pl-no-wrap text-center">Prob.</SortableHeader>
                      <SortableHeader field="dealValue" sortConfig={sortConfig} onSort={handleSort} align="right" className="pl-table-head-cell px-0.5 pl-no-wrap text-right">Value</SortableHeader>
                      <SortableHeader field="cost" sortConfig={sortConfig} onSort={handleSort} align="right" className="pl-table-head-cell px-0.5 pl-no-wrap text-right">Cost</SortableHeader>
                      <SortableHeader
                        field="margin"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        align="right"
                        className="pl-table-head-cell px-0.5 pl-no-wrap text-right"
                        title="Margin %"
                      >
                        Mar.%
                      </SortableHeader>
                      <SortableHeader
                        field="marginAmt"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        align="right"
                        className="pl-table-head-cell px-0.5 pl-no-wrap text-right"
                        title="Margin amount (deal value − cost)"
                      >
                        Amt
                      </SortableHeader>
                      <SortableHeader
                        field="invoiced"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        align="right"
                        className="pl-table-head-cell pl-no-wrap text-right"
                        title="Σ tranche amounts (issued / partially paid / paid / overdue) ÷ Deal Value (total_revenue_chf), clamped to 0–100%."
                      >
                        Inv. %
                      </SortableHeader>
                      <SortableHeader field="currency" sortConfig={sortConfig} onSort={handleSort} align="center" className="pl-table-head-cell px-0.5 pl-no-wrap text-center">Cur.</SortableHeader>
                      <SortableHeader
                        field="expectedClose"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        align="center"
                        className="pl-table-head-cell whitespace-normal px-1 text-center text-[10px] font-medium leading-tight"
                        title="Expected close (deal closure) and service rendered date"
                      >
                        Close / SR
                      </SortableHeader>
                      <SortableHeader field="updated" sortConfig={sortConfig} onSort={handleSort} align="center" className="pl-table-head-cell px-0.5 pl-no-wrap text-center">Upd.</SortableHeader>
                      <TableHead className={`pl-table-head-cell pl-no-wrap text-right ${STICKY_ACT_TH}`}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeTableRows.flatMap((item, idx) => {
                      if (item.kind === 'subtotal') {
                        const st = computeLinkSubtotals(item.deals);
                        const gid = String(item.deals[0]?.link_group_id || '').slice(0, 8);
                        return [(
                          <TableRow
                            key={`sub-a-${idx}-${gid}`}
                            className="bg-indigo-50/90 hover:bg-indigo-50"
                            data-testid="deal-link-subtotal-desktop"
                          >
                            <TableCell className={`pl-cell-center ${STICKY_CB_SUB}`} />
                            <TableCell colSpan={2} className="pl-cell-text text-xs font-semibold text-indigo-950">
                              Linked subtotal ({item.deals.length} projects)
                            </TableCell>
                            <TableCell className="pl-cell-center text-xs text-indigo-800">—</TableCell>
                            <TableCell className="pl-cell-center pl-no-wrap text-xs font-mono text-indigo-950">
                              {formatPercent(st.probDisplay)}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap text-xs font-mono text-indigo-950">
                              {formatNumber(st.rev)} {st.cur}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap text-xs font-mono text-indigo-950">
                              {formatNumber(st.cost)} {st.cur}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5 text-xs">
                              <MarginCell pct={st.marginPct} target={30} />
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5 text-xs font-mono text-indigo-950">
                              <MarginAmtCell amountChf={st.marginAmt} marginPct={st.marginPct} target={30} />
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap text-xs font-mono text-indigo-950">
                              {formatPercent(st.invPct)}
                            </TableCell>
                            <TableCell className="pl-cell-center text-xs text-indigo-800">{st.cur}</TableCell>
                            <TableCell className="pl-cell-date text-xs text-indigo-800">—</TableCell>
                            <TableCell className="pl-cell-date text-xs text-indigo-800">—</TableCell>
                            <TableCell className={`px-1 text-right text-xs text-indigo-800 ${STICKY_ACT_SUB}`}>—</TableCell>
                          </TableRow>
                        )];
                      }
                      const deal = item.deal;
                      const isEditing = editingId === deal.id;
                      const draft = isEditing ? editDraft : null;
                      const draftRevenue = Number(draft?.deal_value_chf ?? deal.total_revenue_chf ?? 0);
                      const draftCost = Number(draft?.deal_cost_chf ?? deal.total_cost_chf ?? 0);
                      const marginPct = draftRevenue > 0 ? ((draftRevenue - draftCost) / draftRevenue) * 100 : 0;
                      const marginAmtChf = Math.round((draftRevenue - draftCost) * 100) / 100;
                      const stageValue = draft?.stage || deal.stage || 'Demand';
                      const stageP = isEditing ? getStageWinProbability(stageValue) : (deal.pipeline_stage_probability ?? 0);
                      const rowCurrency = (isEditing ? draft?.core_currency : deal.core_currency) || 'CHF';

                      return [(
                        <Fragment key={deal.id}>
                          <TableRow
                            className={`pl-row group transition-colors duration-150 ease-in-out hover:bg-slate-50 ${isEditing ? 'cursor-default' : 'cursor-pointer'}`}
                            onClick={() => {
                              if (!isEditing) onSelect(deal.id);
                            }}
                            data-testid={`deal-row-${deal.id}`}
                          >
                            <TableCell
                              className={`pl-cell-center align-middle ${STICKY_CB_TD}`}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              role="presentation"
                            >
                              <Checkbox
                                checked={selectedIds.has(deal.id)}
                                onCheckedChange={(v) => toggleSelect(deal.id, v === true)}
                                disabled={loading || linkBusy}
                                aria-label={`Select ${deal.project_name || 'deal'}`}
                              />
                            </TableCell>
                            <TableCell className="pl-cell-text min-w-0 pl-wrap px-1">
                              {isEditing ? (
                                <Input
                                  value={draft.project_name}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, project_name: e.target.value }))}
                                  className="h-9"
                                />
                              ) : (
                                <span className="pl-two-line text-[13px] font-medium text-gray-900" data-testid={`deal-project-${deal.id}`}>
                                  {deal.project_name || 'Untitled'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="pl-cell-text min-w-0 pl-wrap px-1">
                              {isEditing ? (
                                <Input
                                  value={draft.client_name}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, client_name: e.target.value }))}
                                  className="h-9"
                                />
                              ) : (
                                <span className="pl-two-line text-[13px] text-gray-600" data-testid={`deal-client-${deal.id}`}>
                                  {deal.client_name || '\u2014'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="pl-cell-center pl-no-wrap">
                              {isEditing ? (
                                <Select value={draft.stage} onValueChange={(v) => setEditDraft((d) => ({ ...d, stage: v }))}>
                                  <SelectTrigger className="h-8 w-full min-w-0 max-w-[5.5rem] px-1.5 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {DEAL_STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <StageBadge stage={deal.stage} />
                              )}
                            </TableCell>
                            <TableCell className="pl-cell-center pl-no-wrap text-xs text-gray-500">
                              {formatPercent(stageP * 100)}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={draft.deal_value_chf}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, deal_value_chf: e.target.value }))}
                                  className="h-9 w-[80px] min-w-0 text-right text-xs"
                                />
                              ) : (
                                <span className="text-xs">{formatNumber(deal.total_revenue_chf)} {rowCurrency}</span>
                              )}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5 text-gray-700">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={draft.deal_cost_chf}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, deal_cost_chf: e.target.value }))}
                                  className="h-9 w-[76px] min-w-0 text-right text-xs"
                                />
                              ) : (
                                <span className="text-xs">{formatNumber(deal.total_cost_chf)} {rowCurrency}</span>
                              )}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5">
                              <MarginCell pct={marginPct} target={deal.margin_target_pct || 30} />
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5">
                              <MarginAmtCell amountChf={marginAmtChf} marginPct={marginPct} target={deal.margin_target_pct || 30} />
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap text-gray-800" data-testid={`deal-invoiced-pct-${deal.id}`}>
                              <span className="text-xs">{formatPercent(deal.invoiced_percentage ?? 0)}</span>
                            </TableCell>
                            <TableCell className="pl-cell-center max-w-[76px] overflow-hidden px-0.5 pl-no-wrap">
                              {isEditing ? (
                                <Select value={draft.core_currency} onValueChange={(v) => setEditDraft((d) => ({ ...d, core_currency: v }))}>
                                  <SelectTrigger className="h-8 w-full min-w-0 max-w-[4.5rem] px-1.5 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-xs text-gray-500">{deal.core_currency || 'CHF'}</span>
                              )}
                            </TableCell>
                            <TableCell
                              className="min-w-0 overflow-hidden px-0.5 text-center align-middle text-xs text-gray-500 tabular-nums"
                              style={{ width: DEAL_LIB_CLOSE_SR_COL_PX, maxWidth: DEAL_LIB_CLOSE_SR_COL_PX }}
                            >
                              {isEditing ? (
                                <CloseSrDateEditors
                                  draft={draft}
                                  dealId={deal.id}
                                  onChange={(field, value) => setEditDraft((d) => ({ ...d, [field]: value }))}
                                />
                              ) : (
                                <CloseSrDateReadout deal={deal} />
                              )}
                            </TableCell>
                            <TableCell className="overflow-hidden px-0.5 text-center pl-no-wrap text-xs text-gray-500 tabular-nums">
                              <UpdatedAtCell dateStr={deal.updated_at} testId={`deal-updated-${deal.id}`} />
                            </TableCell>
                            <TableCell
                              className={`px-1 text-right ${STICKY_ACT_TD}`}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              role="presentation"
                            >
                              <div
                                className="flex flex-shrink-0 justify-end gap-0 opacity-100"
                              >
                                {isEditing ? (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => saveEdit(deal.id)}
                                      disabled={savingEdit || loading}
                                      className="h-7 w-7 text-green-600 hover:bg-green-50"
                                      title="Save"
                                      data-testid={`save-deal-${deal.id}`}
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={cancelEdit}
                                      disabled={savingEdit || loading}
                                      className="h-7 w-7 text-gray-500 hover:bg-gray-100"
                                      title="Cancel"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        beginEdit(deal);
                                      }}
                                      disabled={loading}
                                      className="h-7 w-7 text-gray-500 hover:bg-gray-100"
                                      data-testid={`edit-deal-${deal.id}`}
                                      title="Inline edit"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onSelect(deal.id);
                                      }}
                                      disabled={loading}
                                      className="h-7 w-7 text-gray-500 hover:bg-gray-100"
                                      title="Open full editor"
                                    >
                                      <ArrowLeft className="h-3.5 w-3.5 rotate-180" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDuplicate(deal.id);
                                      }}
                                      disabled={loading}
                                      className="h-7 w-7 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                                      data-testid={`duplicate-deal-${deal.id}`}
                                      title="Duplicate deal"
                                    >
                                      <Copy className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        confirmDeleteDeal(deal.id);
                                      }}
                                      disabled={loading}
                                      className="h-7 w-7 text-red-400 hover:bg-red-50 hover:text-red-600"
                                      data-testid={`delete-deal-${deal.id}`}
                                      title="Delete deal"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      )];
                    })}
                    {activeFooterStats ? (
                      <TableRow
                        key="deal-library-active-footer"
                        className="border-t-2 border-slate-300 bg-slate-100/95 hover:bg-slate-100"
                        data-testid="deal-library-active-total"
                      >
                        <TableCell className={`pl-cell-center align-middle ${STICKY_CB_TD} border-t-2 border-slate-300 bg-slate-100/95`} />
                        <TableCell colSpan={2} className="border-t-2 border-slate-300 bg-slate-100/95 pl-cell-text min-w-0 px-1">
                          <span className="text-[13px] font-semibold text-slate-900">Total · visible active</span>
                          <span className="mt-0.5 block text-[10px] font-normal leading-snug text-slate-600">
                            {activeFooterStats.n} deal{activeFooterStats.n === 1 ? '' : 's'} · excludes Rejected/Archived; each deal once (linked subtotals not added again).
                            Invoiced % and probability are revenue-weighted{activeFooterStats.lines.length > 1 ? ' per currency' : ''}.
                          </span>
                        </TableCell>
                        <TableCell className="border-t-2 border-slate-300 bg-slate-100/95 pl-cell-center pl-no-wrap text-xs text-slate-600">
                          —
                        </TableCell>
                        <TableCell className="border-t-2 border-slate-300 bg-slate-100/95 pl-cell-center pl-no-wrap">
                          <div className="flex flex-col items-center gap-0.5">
                            {activeFooterStats.lines.map((ln) => (
                              <span key={ln.cur} className="text-[11px] font-mono font-semibold tabular-nums text-slate-800">
                                {formatPercent(ln.probDisplay)}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="border-t-2 border-slate-300 bg-slate-100/95 pl-cell-num pl-no-wrap px-0.5">
                          <div className="flex flex-col items-end gap-0.5">
                            {activeFooterStats.lines.map((ln) => (
                              <span key={`fv-${ln.cur}`} className="text-xs font-mono font-semibold tabular-nums text-slate-900">
                                {formatNumber(ln.rev)} {ln.cur}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="border-t-2 border-slate-300 bg-slate-100/95 pl-cell-num pl-no-wrap px-0.5">
                          <div className="flex flex-col items-end gap-0.5">
                            {activeFooterStats.lines.map((ln) => (
                              <span key={`fc-${ln.cur}`} className="text-xs font-mono font-semibold tabular-nums text-slate-800">
                                {formatNumber(ln.cost)} {ln.cur}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="border-t-2 border-slate-300 bg-slate-100/95 pl-cell-num pl-no-wrap px-0.5">
                          <div className="flex flex-col items-end gap-0.5">
                            {activeFooterStats.lines.map((ln) => (
                              <MarginCell key={`fm-${ln.cur}`} pct={ln.marginPct} target={30} />
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="border-t-2 border-slate-300 bg-slate-100/95 pl-cell-num pl-no-wrap px-0.5">
                          <div className="flex flex-col items-end gap-0.5">
                            {activeFooterStats.lines.map((ln) => (
                              <MarginAmtCell
                                key={`fa-${ln.cur}`}
                                amountChf={ln.marginAmt}
                                marginPct={ln.marginPct}
                                target={30}
                              />
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="border-t-2 border-slate-300 bg-slate-100/95 pl-cell-num pl-no-wrap">
                          <div className="flex flex-col items-end gap-0.5">
                            {activeFooterStats.lines.map((ln) => (
                              <span key={`fi-${ln.cur}`} className="text-xs font-mono font-semibold tabular-nums text-slate-800">
                                {formatPercent(ln.invPct)}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="border-t-2 border-slate-300 bg-slate-100/95 pl-cell-center pl-no-wrap">
                          {activeFooterStats.lines.length === 1 ? (
                            <span className="text-xs font-medium text-slate-700">{activeFooterStats.lines[0].cur}</span>
                          ) : (
                            <span
                              className="max-w-[3.5rem] text-center text-[10px] font-medium leading-tight text-slate-600"
                              title="Multiple core currencies in the filtered list"
                            >
                              Mixed
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="border-t-2 border-slate-300 bg-slate-100/95 pl-cell-date text-xs text-slate-600">
                          —
                        </TableCell>
                        <TableCell className="border-t-2 border-slate-300 bg-slate-100/95 pl-cell-date text-xs text-slate-600">
                          —
                        </TableCell>
                        <TableCell
                          className={`border-t-2 border-slate-300 bg-slate-100/95 px-1 text-right text-xs text-slate-600 ${STICKY_ACT_TD}`}
                        >
                          —
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {segregatedCount > 0 ? (
                      <TableRow key="deal-row-segregated-heading" className="bg-slate-50 hover:bg-slate-50">
                        <TableCell colSpan={DEAL_LIBRARY_TABLE_COL_COUNT} className="h-11 px-3 text-xs font-semibold text-slate-600">
                          Segregated deals (Rejected / Archived) — excluded from active forecasting and sales reporting.
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {segregatedTableRows.flatMap((item, idx) => {
                      if (item.kind === 'subtotal') {
                        const st = computeLinkSubtotals(item.deals);
                        const gid = String(item.deals[0]?.link_group_id || '').slice(0, 8);
                        return [(
                          <TableRow
                            key={`sub-s-${idx}-${gid}`}
                            className="bg-indigo-50/90 hover:bg-indigo-50"
                            data-testid="deal-link-subtotal-desktop-seg"
                          >
                            <TableCell className={`pl-cell-center ${STICKY_CB_SUB}`} />
                            <TableCell colSpan={2} className="pl-cell-text text-xs font-semibold text-indigo-950">
                              Linked subtotal ({item.deals.length} projects)
                            </TableCell>
                            <TableCell className="pl-cell-center text-xs text-indigo-800">—</TableCell>
                            <TableCell className="pl-cell-center pl-no-wrap text-xs font-mono text-indigo-950">
                              {formatPercent(st.probDisplay)}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap text-xs font-mono text-indigo-950">
                              {formatNumber(st.rev)} {st.cur}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap text-xs font-mono text-indigo-950">
                              {formatNumber(st.cost)} {st.cur}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5 text-xs">
                              <MarginCell pct={st.marginPct} target={30} />
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5 text-xs font-mono text-indigo-950">
                              <MarginAmtCell amountChf={st.marginAmt} marginPct={st.marginPct} target={30} />
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap text-xs font-mono text-indigo-950">
                              {formatPercent(st.invPct)}
                            </TableCell>
                            <TableCell className="pl-cell-center text-xs text-indigo-800">{st.cur}</TableCell>
                            <TableCell className="pl-cell-date text-xs text-indigo-800">—</TableCell>
                            <TableCell className="pl-cell-date text-xs text-indigo-800">—</TableCell>
                            <TableCell className={`px-1 text-right text-xs text-indigo-800 ${STICKY_ACT_SUB}`}>—</TableCell>
                          </TableRow>
                        )];
                      }
                      const deal = item.deal;
                      const isEditing = editingId === deal.id;
                      const draft = isEditing ? editDraft : null;
                      const draftRevenue = Number(draft?.deal_value_chf ?? deal.total_revenue_chf ?? 0);
                      const draftCost = Number(draft?.deal_cost_chf ?? deal.total_cost_chf ?? 0);
                      const marginPct = draftRevenue > 0 ? ((draftRevenue - draftCost) / draftRevenue) * 100 : 0;
                      const marginAmtChf = Math.round((draftRevenue - draftCost) * 100) / 100;
                      const stageValue = draft?.stage || deal.stage || 'Demand';
                      const stageP = isEditing ? getStageWinProbability(stageValue) : (deal.pipeline_stage_probability ?? 0);
                      const rowCurrency = (isEditing ? draft?.core_currency : deal.core_currency) || 'CHF';

                      return [(
                        <Fragment key={`seg-${deal.id}`}>
                          <TableRow
                            className={`pl-row group transition-colors duration-150 ease-in-out hover:bg-slate-50 ${isEditing ? 'cursor-default' : 'cursor-pointer'}`}
                            onClick={() => {
                              if (!isEditing) onSelect(deal.id);
                            }}
                            data-testid={`deal-row-${deal.id}`}
                          >
                            <TableCell
                              className={`pl-cell-center align-middle ${STICKY_CB_TD}`}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              role="presentation"
                            >
                              <Checkbox
                                checked={selectedIds.has(deal.id)}
                                onCheckedChange={(v) => toggleSelect(deal.id, v === true)}
                                disabled={loading || linkBusy}
                                aria-label={`Select ${deal.project_name || 'deal'}`}
                              />
                            </TableCell>
                            <TableCell className="pl-cell-text min-w-0 pl-wrap px-1">
                              {isEditing ? (
                                <Input
                                  value={draft.project_name}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, project_name: e.target.value }))}
                                  className="h-9"
                                />
                              ) : (
                                <span className="pl-two-line text-[13px] font-medium text-gray-900" data-testid={`deal-project-${deal.id}`}>
                                  {deal.project_name || 'Untitled'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="pl-cell-text min-w-0 pl-wrap px-1">
                              {isEditing ? (
                                <Input
                                  value={draft.client_name}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, client_name: e.target.value }))}
                                  className="h-9"
                                />
                              ) : (
                                <span className="pl-two-line text-[13px] text-gray-600" data-testid={`deal-client-${deal.id}`}>
                                  {deal.client_name || '\u2014'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="pl-cell-center pl-no-wrap">
                              {isEditing ? (
                                <Select value={draft.stage} onValueChange={(v) => setEditDraft((d) => ({ ...d, stage: v }))}>
                                  <SelectTrigger className="h-8 w-full min-w-0 max-w-[5.5rem] px-1.5 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {DEAL_STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <StageBadge stage={deal.stage} />
                              )}
                            </TableCell>
                            <TableCell className="pl-cell-center pl-no-wrap text-xs text-gray-500">
                              {formatPercent(stageP * 100)}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={draft.deal_value_chf}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, deal_value_chf: e.target.value }))}
                                  className="h-9 w-[80px] min-w-0 text-right text-xs"
                                />
                              ) : (
                                <span className="text-xs">{formatNumber(deal.total_revenue_chf)} {rowCurrency}</span>
                              )}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5 text-gray-700">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={draft.deal_cost_chf}
                                  onChange={(e) => setEditDraft((d) => ({ ...d, deal_cost_chf: e.target.value }))}
                                  className="h-9 w-[76px] min-w-0 text-right text-xs"
                                />
                              ) : (
                                <span className="text-xs">{formatNumber(deal.total_cost_chf)} {rowCurrency}</span>
                              )}
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5">
                              <MarginCell pct={marginPct} target={deal.margin_target_pct || 30} />
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap px-0.5">
                              <MarginAmtCell amountChf={marginAmtChf} marginPct={marginPct} target={deal.margin_target_pct || 30} />
                            </TableCell>
                            <TableCell className="pl-cell-num pl-no-wrap text-gray-800" data-testid={`deal-invoiced-pct-${deal.id}`}>
                              <span className="text-xs">{formatPercent(deal.invoiced_percentage ?? 0)}</span>
                            </TableCell>
                            <TableCell className="pl-cell-center max-w-[76px] overflow-hidden px-0.5 pl-no-wrap">
                              {isEditing ? (
                                <Select value={draft.core_currency} onValueChange={(v) => setEditDraft((d) => ({ ...d, core_currency: v }))}>
                                  <SelectTrigger className="h-8 w-full min-w-0 max-w-[4.5rem] px-1.5 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className="text-xs text-gray-500">{deal.core_currency || 'CHF'}</span>
                              )}
                            </TableCell>
                            <TableCell
                              className="min-w-0 overflow-hidden px-0.5 text-center align-middle text-xs text-gray-500 tabular-nums"
                              style={{ width: DEAL_LIB_CLOSE_SR_COL_PX, maxWidth: DEAL_LIB_CLOSE_SR_COL_PX }}
                            >
                              {isEditing ? (
                                <CloseSrDateEditors
                                  draft={draft}
                                  dealId={deal.id}
                                  onChange={(field, value) => setEditDraft((d) => ({ ...d, [field]: value }))}
                                />
                              ) : (
                                <CloseSrDateReadout deal={deal} />
                              )}
                            </TableCell>
                            <TableCell className="overflow-hidden px-0.5 text-center pl-no-wrap text-xs text-gray-500 tabular-nums">
                              <UpdatedAtCell dateStr={deal.updated_at} testId={`deal-updated-${deal.id}`} />
                            </TableCell>
                            <TableCell
                              className={`px-1 text-right ${STICKY_ACT_TD}`}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              role="presentation"
                            >
                              <div
                                className="flex flex-shrink-0 justify-end gap-0 opacity-100"
                              >
                                {isEditing ? (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => saveEdit(deal.id)}
                                      disabled={savingEdit || loading}
                                      className="h-7 w-7 text-green-600 hover:bg-green-50"
                                      title="Save"
                                      data-testid={`save-deal-${deal.id}`}
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={cancelEdit}
                                      disabled={savingEdit || loading}
                                      className="h-7 w-7 text-gray-500 hover:bg-gray-100"
                                      title="Cancel"
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        beginEdit(deal);
                                      }}
                                      disabled={loading}
                                      className="h-7 w-7 text-gray-500 hover:bg-gray-100"
                                      data-testid={`edit-deal-${deal.id}`}
                                      title="Inline edit"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onSelect(deal.id);
                                      }}
                                      disabled={loading}
                                      className="h-7 w-7 text-gray-500 hover:bg-gray-100"
                                      title="Open full editor"
                                    >
                                      <ArrowLeft className="h-3.5 w-3.5 rotate-180" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDuplicate(deal.id);
                                      }}
                                      disabled={loading}
                                      className="h-7 w-7 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                                      data-testid={`duplicate-deal-${deal.id}`}
                                      title="Duplicate deal"
                                    >
                                      <Copy className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        confirmDeleteDeal(deal.id);
                                      }}
                                      disabled={loading}
                                      className="h-7 w-7 text-red-400 hover:bg-red-50 hover:text-red-600"
                                      data-testid={`delete-deal-${deal.id}`}
                                      title="Delete deal"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      )];
                    })}
                  </TableBody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
